import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Play, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import EnquiryForm from '../components/sections/EnquiryForm';
import CustomerVideoReviews from '../components/sections/CustomerVideoReviews';
import { COMPANY } from '../mock';
import { Button } from '../components/ui/button';
import { MessageCircle } from 'lucide-react';

const ALL_TRAVEL_VIDEOS = [
  { id: 'meO8ij1EA4g', title: 'The Ultimate Himalayan Journey', destination: 'North India', category: 'Himachal' },
  { id: 'REF4iExPRSQ', title: 'Kashmir Magic & Landscapes', destination: 'Kashmir', category: 'Kashmir' },
  { id: '8tdDWRnGDXs', title: 'Leh-Ladakh Biking Expedition', destination: 'Ladakh', category: 'Ladakh' },
  { id: 'mefNxRfbJog', title: 'Gulmarg Snow Adventure', destination: 'Kashmir', category: 'Kashmir' },
  { id: 'ToWMc8trRTc', title: 'Srinagar: City of Lakes', destination: 'Kashmir', category: 'Kashmir' },
  { id: '2rvbahofSNc', title: 'Pangong Tso Blue Waters', destination: 'Ladakh', category: 'Ladakh' },
  { id: 'oHMUfTrdRgs', title: 'Char Dham Yatra Spiritual Journey', destination: 'Uttarakhand', category: 'Uttarakhand' },
  { id: '3EgJpa8_qUU', title: 'Spiti Valley Cold Desert', destination: 'Himachal', category: 'Himachal' },
  { id: 'U71rfKKMoTQ', title: 'Manali: Adventure Capital', destination: 'Himachal', category: 'Himachal' },
  { id: 'TH1KKGIunCY', title: 'Shimla: The Queen of Hills', destination: 'Himachal', category: 'Himachal' },
];

const CATEGORIES = ['All', 'Himachal', 'Kashmir', 'Ladakh', 'Uttarakhand', 'Reviews'];
const VIDEOS_PER_PAGE = 9;

export default function TravelDiaries() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [playingId, setPlayingId] = useState(null);

  const filteredVideos = activeCategory === 'All' 
    ? ALL_TRAVEL_VIDEOS 
    : ALL_TRAVEL_VIDEOS.filter(v => v.category === activeCategory);
  
  const totalPages = Math.ceil(filteredVideos.length / VIDEOS_PER_PAGE);
  const paginatedVideos = filteredVideos.slice(
    (currentPage - 1) * VIDEOS_PER_PAGE,
    currentPage * VIDEOS_PER_PAGE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory]);

  return (
    <div>
      <Helmet>
        <title>Travel Diaries | Mountains Chain Travel</title>
        <meta name="description" content="Watch real travel stories and videos from our happy travelers. Experience the magic of Himalayas through their eyes." />
        <meta property="og:title" content="Travel Diaries | Mountains Chain Travel" />
        <meta property="og:description" content="Watch real travel stories and videos from our happy travelers." />
      </Helmet>

      {/* Hero */}
      <section className="relative min-h-[40vh] flex items-center overflow-hidden bg-brand-blue">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-blue via-brand-blue/95 to-brand-blue/80" />
        <div className="relative z-20 max-w-7xl mx-auto px-6 py-16 w-full">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-brand-orange rounded-full text-white text-[12px] font-bold mb-4">
              <span className="tracking-wide">Travel Diaries</span>
            </div>
            <h1 className="font-poppins text-[36px] lg:text-[48px] text-white leading-[1.1] font-extrabold">
              Your Journey <span className="text-brand-orange">Visualized</span>
            </h1>
            <p className="text-white/80 text-[16px] lg:text-[18px] mt-4 max-w-xl leading-relaxed">
              Real travel stories and videos from our happy travelers. Experience the magic of Himalayas through their eyes.
            </p>
          </div>
        </div>
      </section>

      <CustomerVideoReviews />

      {/* Filter & Videos */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-[13px] font-semibold transition-colors ${
                activeCategory === cat 
                  ? 'bg-brand-orange text-white' 
                  : 'bg-brand-grey text-brand-blue hover:bg-brand-greyDark'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Videos Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {paginatedVideos.map((video, i) => (
            <div key={video.id} className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-1">
              <div className="aspect-video relative bg-brand-blue">
                {playingId === video.id ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${video.id}?autoplay=1&rel=0&modestbranding=1`}
                    title={video.title}
                    className="absolute inset-0 w-full h-full border-0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <div 
                    className="absolute inset-0 w-full h-full cursor-pointer"
                    onClick={() => setPlayingId(video.id)}
                  >
                    <img 
                      src={`https://img.youtube.com/vi/${video.id}/hqdefault.jpg`} 
                      alt={video.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-brand-blue/20 group-hover:bg-brand-blue/10 transition-colors" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full bg-white/95 text-brand-orange shadow-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Play size={28} className="ml-1 fill-brand-orange" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-2 py-0.5 bg-brand-orange/90 rounded text-white text-[10px] font-bold uppercase">{video.category}</span>
                </div>
                <h3 className="text-white font-poppins font-bold text-[14px] leading-snug">{video.title}</h3>
                <p className="text-white/70 text-[11px] mt-0.5">{video.destination}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-10">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="w-10 h-10 rounded-full bg-brand-grey hover:bg-brand-greyDark flex items-center justify-center text-brand-blue transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={20} />
            </button>
            <div className="flex items-center gap-2">
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`w-8 h-8 rounded-full text-[13px] font-semibold transition-colors ${
                    currentPage === i + 1 
                      ? 'bg-brand-orange text-white' 
                      : 'bg-brand-grey text-brand-blue hover:bg-brand-greyDark'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="w-10 h-10 rounded-full bg-brand-grey hover:bg-brand-greyDark flex items-center justify-center text-brand-blue transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        )}

        {/* Video Count */}
        <div className="text-center mt-6 text-brand-blue/50 text-[13px]">
          Showing {paginatedVideos.length} of {filteredVideos.length} videos
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-blue py-12">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-poppins text-[28px] lg:text-[36px] text-white font-bold">Ready to Create Your Own Story?</h2>
          <p className="text-white/70 text-[15px] mt-3 max-w-xl mx-auto">Book your dream trip today and become part of our travel diaries.</p>
          <div className="flex flex-wrap justify-center gap-3 mt-6">
            <a href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent('Hi Mountains Chain Travel! 👋\n\nI\'d love to plan a trip and share my travel story.\n\nPlease share available packages.\n\nThanks!')}`} target="_blank" rel="noreferrer">
              <Button className="bg-brand-orange hover:bg-brand-orangeDark text-white h-12 px-7 rounded-xl text-[15px] font-bold btn-glow shadow-xl shadow-brand-orange/30">
                <MessageCircle size={18} className="mr-2"/>Book Now
              </Button>
            </a>
          </div>
        </div>
      </section>

      <EnquiryForm />
    </div>
  );
}
